import type { AgentOutputTurnBuf, AgentOutputTurnFragmentBuf, AgentStatusTurnBuf, AgentStatusTurnFileCopyRequestedBuf, AgentStatusTurnFileCopyRespondedBuf, AgentStatusTurnFileCreateRequestedBuf, AgentStatusTurnFileCreateRespondedBuf, AgentStatusTurnFileEditRequestedBuf, AgentStatusTurnFileEditRespondedBuf, AgentStatusTurnFileLoadRequestedBuf, AgentStatusTurnFileLoadRespondedBuf, AgentStatusTurnFileMoveRequestedBuf, AgentStatusTurnFileMoveRespondedBuf, AgentStatusTurnFileUpdateRequestedBuf, AgentStatusTurnFileUpdateRespondedBuf, AgentStatusTurnPartBuf, AgentStatusTurnSearchListRequestedBuf, AgentStatusTurnSearchListRespondedBuf, AgentTurnBuf } from "~/classes/generated/conversation"
import type { AgentStatusTurnSearchListRequested } from "~/models/polyschema/AgentStatusTurnSearchListRequested"
import type { AgentStatusTurnSearchListResponded } from "~/models/polyschema/AgentStatusTurnSearchListResponded"

import { ConversationBuf, ConversationPropertyBuf, ConversationTurnBuf, UserInputTurnBuf, UserInputTurnFragmentBuf, UserInputTurnFragmentFileBuf, UserTurnBuf } from "~/classes/generated/conversation"
import { parseFileCopyRequestDatagramBuf, parseFileCopyResponseDatagramBuf } from "~/classes/handlers/file/copy"
import { parseFileCreateRequestDatagramBuf, parseFileCreateResponseDatagramBuf } from "~/classes/handlers/file/create"
import { parseFileDownloadRequestDatagramBuf, parseFileDownloadResponseDatagramBuf } from "~/classes/handlers/file/download"
import { parseFileEditRequestDatagramBuf, parseFileEditResponseDatagramBuf } from "~/classes/handlers/file/edit"
import { parseFileMoveRequestDatagramBuf, parseFileMoveResponseDatagramBuf } from "~/classes/handlers/file/move"
import { parseFileUpdateRequestDatagramBuf, parseFileUpdateResponseDatagramBuf } from "~/classes/handlers/file/update"
import { parseSearchListPageResponseDatagramBuf, parseSearchListRequestDatagramBuf } from "~/classes/handlers/search/list"

import { maybe } from "../helpers"
import { parseDatagramError, parseVector } from "./datagram"
import { addEntityPropertySelectionToBuf, addEntityPropertyTimestampToBuf, addEntityPropertyValueToBuf, asEntityPropertyStatusBuf, parseEntityPropertySchemaValue, parseEntityPropertySelection, parseEntityPropertyStatus, parseEntityPropertyTimestampBuf } from "./entity"
import { addFileToBuf, parseFile } from "./file"
import { _parseEntityId, addConversationIdToBuf, addConversationPropertyIdToBuf, addFileIdToBuf, addUserIdToBuf, parseConversationId, parseEntityPropertyId, parseFileId, parseFilePropertyId, parseUserId } from "./id"

export function parseConversationProperty(buf: ConversationPropertyBuf) {
  // For some unknown reason (as of 11-7-2024), when using tauri channels, there is an
  // odd bug where the `valueArray` return null. While we figure that out, here is a kludge
  let value = buf.valueArray()
  if (!value?.length)
    value = new Uint8Array(parseVector(x => buf.value(x)!, buf.valueLength()))
  const conversationProperty: ConversationProperty = {
    id: parseConversationId(buf.id()!),
    propertyId: parseEntityPropertyId(buf.propertyId()!),
    version: buf.version(),
    schema: buf.schema(),
    value: parseEntityPropertySchemaValue(buf.schema(), value),
    timestamp: parseEntityPropertyTimestampBuf(buf.timestamp()!),
    deleted: buf.deleted(),
    author: _parseEntityId(buf.author()!),
    status: parseEntityPropertyStatus(buf.status()),
    sharedWithUserId: maybe(parseUserId)(buf.sharedWithUserId()),
    epochId: buf.epochId() ?? undefined,
  }
  return conversationProperty
}

export function addConversationPropertyToBuf(
  fbb: DatagramBufferBuilder,
  property: ConversationProperty,
) {
  const value = addEntityPropertyValueToBuf(fbb, property.value)
  const conversationId = addConversationIdToBuf(fbb, property.id)
  const propertyId = addConversationPropertyIdToBuf(fbb, property.propertyId)
  const timestamp = addEntityPropertyTimestampToBuf(fbb, property.timestamp)
  const author = addUserIdToBuf(fbb, property.author)
  let sharedWithUserId
  if (property.sharedWithUserId !== undefined)
    sharedWithUserId = addUserIdToBuf(fbb, property.sharedWithUserId)

  ConversationPropertyBuf.startConversationPropertyBuf(fbb)
  ConversationPropertyBuf.addId(fbb, conversationId)
  ConversationPropertyBuf.addPropertyId(fbb, propertyId)
  ConversationPropertyBuf.addVersion(fbb, property.version)
  ConversationPropertyBuf.addSchema(fbb, property.schema)
  ConversationPropertyBuf.addValue(fbb, value)
  ConversationPropertyBuf.addTimestamp(fbb, timestamp)
  if (property.deleted)
    ConversationPropertyBuf.addDeleted(fbb, property.deleted)
  ConversationPropertyBuf.addStatus(fbb, asEntityPropertyStatusBuf(property.status))
  if (property.epochId !== undefined)
    ConversationPropertyBuf.addEpochId(fbb, property.epochId)
  if (isDefined(sharedWithUserId))
    ConversationPropertyBuf.addSharedWithUserId(fbb, sharedWithUserId)
  ConversationPropertyBuf.addAuthor(fbb, author)
  return ConversationPropertyBuf.endConversationPropertyBuf(fbb)
}

export function parseConversation(buf: ConversationBuf): Conversation {
  const id = parseConversationId(buf.id()!)
  const selection = parseEntityPropertySelection(buf.selection())
  const properties: Record<string, ConversationProperty> = {}
  for (let i = 0; i < buf.propertiesLength(); i++) {
    const property = parseConversationProperty(buf.properties(i)!)
    properties[property.propertyId] = property
  }
  return { id, properties, selection }
}

export function parseMaybeConversation(conversation: ConversationBuf | null) {
  if (conversation)
    return parseConversation(conversation)
}

export function addConversationToBuf(fbb: DatagramBufferBuilder, conversation: Conversation) {
  const innerProperties = Object.values(conversation.properties).map(p => addConversationPropertyToBuf(fbb, p!))
  const properties = ConversationBuf.createPropertiesVector(fbb, innerProperties)
  const conversationId = addConversationIdToBuf(fbb, conversation.id)
  let selection
  if (conversation.selection)
    selection = addEntityPropertySelectionToBuf(fbb, conversation.selection)
  ConversationBuf.startConversationBuf(fbb)
  ConversationBuf.addId(fbb, conversationId)
  ConversationBuf.addProperties(fbb, properties)
  if (selection)
    ConversationBuf.addSelection(fbb, selection)
  return ConversationBuf.endConversationBuf(fbb)
}

export function parseConversationTurn(
  buf: ConversationTurnBuf,
): ConversationTurn {
  if (buf.user() !== null)
    return { user: parseUserTurn(buf.user()!) }
  else if (buf.agent() !== null)
    return { agent: parseAgentTurn(buf.agent()!) }
  throw new Error("Not found")
}

function parseUserTurn(
  buf: UserTurnBuf,
): UserTurn {
  if (buf.input() !== null)
    return { input: parseUserInputTurn(buf.input()!) }
  throw new Error("Unknown user turn type")
}

function parseUserInputTurn(
  buf: UserInputTurnBuf,
): UserInputTurn {
  const fragments = parseVector(
    n => buf.fragments(n)!,
    buf.fragmentsLength(),
  ).map(parseUserInputTurnFragment)
  const searchLocations = parseVector(
    n => buf.searchLocations(n)!,
    buf.searchLocationsLength(),
  ).map(parseFileId)
  const current = maybe(parseFile)(buf.current())
  const userId = maybe(parseUserId)(buf.userId())
  return { fragments, searchLocations, current, userId }
}

function parseUserInputTurnFragment(
  buf: UserInputTurnFragmentBuf,
): UserInputTurnFragment {
  if (buf.text())
    return { text: buf.text()! }
  else if (buf.fileRef()) {
    const fileRef = buf.fileRef()!
    const fileId = parseFileId(fileRef.fileId()!)
    const mediaType = fileRef.mediaType()!
    return { fileRef: { fileId, mediaType } }
  }
  else
    throw new Error("Unknown fragment type")
}

function parseAgentTurn(
  buf: AgentTurnBuf,
): AgentTurn {
  if (buf.connected() !== null)
    return { connected: buf.connected()! }
  else if (buf.disconnected() !== null)
    return { disconnected: buf.disconnected()! }
  else if (buf.output() !== null)
    return { output: parseAgentOutputTurn(buf.output()!) }
  else if (buf.status() !== null)
    return { status: parseAgentStatusTurn(buf.status()!) }
  else if (buf.renamedConversation() !== null)
    return { renamedConversation: buf.renamedConversation()! }
  else if (buf.errored() !== null)
    return { errored: parseDatagramError(buf.errored()!) }
  else if (buf.ended() !== null)
    return { ended: buf.ended()! }
  console.error("Unknown turn type", buf)
  throw new Error("Unknown agent turn type")
}

function parseAgentOutputTurn(
  buf: AgentOutputTurnBuf,
): AgentOutputTurn {
  const fragments = parseVector(
    n => buf.fragments(n)!,
    buf.fragmentsLength(),
  ).map(parseAgentOutputTurnFragment)
  return { fragments }
}

function parseAgentOutputTurnFragment(
  buf: AgentOutputTurnFragmentBuf,
): AgentOutputTurnFragment {
  if (buf.text() !== null)
    return { text: buf.text()! }
  if (buf.fileRef() !== null) {
    const fileRef = buf.fileRef()!
    return {
      fileRef: {
        file: parseFile(fileRef!.file()!),
      },
    }
  } if (buf.fileDetailsRef() !== null) {
    const fileDetailsRef = buf.fileDetailsRef()!
    return {
      fileDetailsRef: {
        file: parseFile(fileDetailsRef.file()!),
        properties: parseVector(
          i => parseFilePropertyId(fileDetailsRef.properties(i)!),
          buf.fileDetailsRef()!.propertiesLength(),
        ),
      },
    }
  }
  if (buf.fileSnippetRef() !== null)
    return { fileSnippetRef: buf.fileSnippetRef()! }

  console.error("Unknown or forbidden fragment type", buf)
  return "unused" as AgentOutputTurnFragment
}

function parseAgentStatusTurn(
  buf: AgentStatusTurnBuf,
): AgentStatusTurn {
  const parts = parseVector(
    n => buf.parts(n)!,
    buf.partsLength(),
  ).map(parseAgentStatusTurnPart)
  return { parts }
}

function parseAgentStatusTurnPart(
  buf: AgentStatusTurnPartBuf,
): AgentStatusTurnPart {
  if (buf.fileLoadRequested() !== null)
    return { fileLoadRequested: parseFileLoadRequested(buf.fileLoadRequested()!) }
  else if (buf.fileLoadResponded() !== null)
    return { fileLoadResponded: parseFileLoadResponded(buf.fileLoadResponded()!) }
  else if (buf.fileCreateRequested() !== null)
    return { fileCreateRequested: parseFileCreateRequested(buf.fileCreateRequested()!) }
  else if (buf.fileCreateResponded() !== null)
    return { fileCreateResponded: parseFileCreateResponded(buf.fileCreateResponded()!) }
  else if (buf.fileMoveRequested() !== null)
    return { fileMoveRequested: parseFileMoveRequested(buf.fileMoveRequested()!) }
  else if (buf.fileMoveResponded() !== null)
    return { fileMoveResponded: parseFileMoveResponded(buf.fileMoveResponded()!) }
  else if (buf.fileUpdateRequested() !== null)
    return { fileUpdateRequested: parseFileUpdateRequested(buf.fileUpdateRequested()!) }
  else if (buf.fileUpdateResponded() !== null)
    return { fileUpdateResponded: parseFileUpdateResponded(buf.fileUpdateResponded()!) }
  else if (buf.fileEditRequested() !== null)
    return { fileEditRequested: parseFileEditRequested(buf.fileEditRequested()!) }
  else if (buf.fileEditResponded() !== null)
    return { fileEditResponded: parseFileEditResponded(buf.fileEditResponded()!) }
  else if (buf.fileCopyRequested() !== null)
    return { fileCopyRequested: parseFileCopyRequested(buf.fileCopyRequested()!) }
  else if (buf.fileCopyResponded() !== null)
    return { fileCopyResponded: parseFileCopyResponded(buf.fileCopyResponded()!) }
  else if (buf.searchListRequested() !== null)
    return { searchListRequested: parseSearchListRequested(buf.searchListRequested()!) }
  else if (buf.searchListResponded() !== null)
    return { searchListResponded: parseSearchListResponded(buf.searchListResponded()!) }
  else
    throw new Error("Unknown agent status turn part type")
}

export type AgentStatusTurnFileLoadRequested = {
  request: FileDownloadRequestDatagram
}

function parseFileLoadRequested(
  buf: AgentStatusTurnFileLoadRequestedBuf,
): AgentStatusTurnFileLoadRequested {
  const request = parseFileDownloadRequestDatagramBuf(buf.request()!)
  return { request }
}

function parseFileLoadResponded(
  buf: AgentStatusTurnFileLoadRespondedBuf,
): AgentStatusTurnFileLoadResponded {
  const response = parseFileDownloadResponseDatagramBuf(buf.response()!)
  return { response }
}

function parseFileCreateRequested(
  buf: AgentStatusTurnFileCreateRequestedBuf,
): AgentStatusTurnFileCreateRequested {
  const request = parseFileCreateRequestDatagramBuf(buf.request()!)
  return { request }
}

function parseFileCreateResponded(
  buf: AgentStatusTurnFileCreateRespondedBuf,
): AgentStatusTurnFileCreateResponded {
  const response = parseFileCreateResponseDatagramBuf(buf.response()!)
  return { response }
}

function parseFileMoveRequested(
  buf: AgentStatusTurnFileMoveRequestedBuf,
): AgentStatusTurnFileMoveRequested {
  const prior = parseFile(buf.prior()!)
  const request = parseFileMoveRequestDatagramBuf(buf.request()!)
  return { prior, request }
}

function parseFileMoveResponded(
  buf: AgentStatusTurnFileMoveRespondedBuf,
): AgentStatusTurnFileMoveResponded {
  const response = parseFileMoveResponseDatagramBuf(buf.response()!)
  return { response }
}

function parseFileUpdateRequested(
  buf: AgentStatusTurnFileUpdateRequestedBuf,
): AgentStatusTurnFileUpdateRequested {
  const prior = parseFile(buf.prior()!)
  const request = parseFileUpdateRequestDatagramBuf(buf.request()!)
  return { prior, request }
}

function parseFileUpdateResponded(
  buf: AgentStatusTurnFileUpdateRespondedBuf,
): AgentStatusTurnFileUpdateResponded {
  const response = parseFileUpdateResponseDatagramBuf(buf.response()!)
  return { response }
}

function parseFileEditRequested(
  buf: AgentStatusTurnFileEditRequestedBuf,
): AgentStatusTurnFileEditRequested {
  const prior = parseFile(buf.prior()!)
  const request = parseFileEditRequestDatagramBuf(buf.request()!)
  return { prior, request }
}

function parseFileEditResponded(
  buf: AgentStatusTurnFileEditRespondedBuf,
): AgentStatusTurnFileEditResponded {
  const response = parseFileEditResponseDatagramBuf(buf.response()!)
  return { response }
}

function parseFileCopyRequested(
  buf: AgentStatusTurnFileCopyRequestedBuf,
): AgentStatusTurnFileCopyRequested {
  const request = parseFileCopyRequestDatagramBuf(buf.request()!)
  return { request }
}

function parseFileCopyResponded(
  buf: AgentStatusTurnFileCopyRespondedBuf,
): AgentStatusTurnFileCopyResponded {
  const response = parseFileCopyResponseDatagramBuf(buf.response()!)
  return { response }
}

function parseSearchListRequested(
  buf: AgentStatusTurnSearchListRequestedBuf,
): AgentStatusTurnSearchListRequested {
  const request = parseSearchListRequestDatagramBuf(buf.request()!)
  return { request }
}

function parseSearchListResponded(
  buf: AgentStatusTurnSearchListRespondedBuf,
): AgentStatusTurnSearchListResponded {
  const response = parseSearchListPageResponseDatagramBuf(buf.response()!)
  return { response }
}

/** We only need to encode the user side, I think? */
export function addConversationTurnToBuf(
  fbb: DatagramBufferBuilder,
  turn: ConversationTurn,
) {
  if ("agent" in turn || !("user" in turn))
    throw new Error("Agent turns shouldn't be serialized on the client?")

  const user = addUserTurnToBuf(fbb, turn.user)

  ConversationTurnBuf.startConversationTurnBuf(fbb)
  ConversationTurnBuf.addUser(fbb, user)
  return ConversationBuf.endConversationBuf(fbb)
}

export function addUserTurnToBuf(
  fbb: DatagramBufferBuilder,
  turn: UserTurn,
) {
  let input
  if (typeof turn !== "string" && "input" in turn)
    input = addUserInputTurnToBuf(fbb, turn.input)

  UserTurnBuf.startUserTurnBuf(fbb)
  if (input)
    UserTurnBuf.addInput(fbb, input)
  return UserTurnBuf.endUserTurnBuf(fbb)
}

export function addUserInputTurnToBuf(
  fbb: DatagramBufferBuilder,
  turn: UserInputTurn,
) {
  const fragmentOffsets = turn.fragments.map(f => addUserInputTurnFragmentToBuf(fbb, f))
  const fragments = UserInputTurnBuf.createFragmentsVector(fbb, fragmentOffsets)
  const searchLocationOffsets = turn.searchLocations.map(f => addFileIdToBuf(fbb, f))
  const searchLocations = UserInputTurnBuf.createSearchLocationsVector(fbb, searchLocationOffsets)
  let current, userId
  if (turn.current)
    current = addFileToBuf(fbb, turn.current)
  if (turn.userId)
    userId = addUserIdToBuf(fbb, turn.userId)

  UserInputTurnBuf.startUserInputTurnBuf(fbb)
  UserInputTurnBuf.addFragments(fbb, fragments)
  UserInputTurnBuf.addSearchLocations(fbb, searchLocations)
  if (current)
    UserInputTurnBuf.addCurrent(fbb, current)
  if (userId)
    UserInputTurnBuf.addUserId(fbb, userId)

  return UserInputTurnBuf.endUserInputTurnBuf(fbb)
}

export function addUserInputTurnFragmentToBuf(
  fbb: DatagramBufferBuilder,
  fragment: UserInputTurnFragment,
) {
  let text
  let fileRef
  if ("text" in fragment)
    text = fbb.createString(fragment.text)
  else if ("fileRef" in fragment) {
    const mediaType = fbb.createString(fragment.fileRef.mediaType)
    const fileId = addFileIdToBuf(fbb, fragment.fileRef.fileId)
    UserInputTurnFragmentFileBuf.startUserInputTurnFragmentFileBuf(fbb)
    UserInputTurnFragmentFileBuf.addFileId(fbb, fileId)
    UserInputTurnFragmentFileBuf.addMediaType(fbb, mediaType)
    fileRef = UserInputTurnFragmentFileBuf.endUserInputTurnFragmentFileBuf(fbb)
  }

  UserInputTurnFragmentBuf.startUserInputTurnFragmentBuf(fbb)
  if (text)
    UserInputTurnFragmentBuf.addText(fbb, text)
  if (fileRef)
    UserInputTurnFragmentBuf.addFileRef(fbb, fileRef)
  return UserInputTurnFragmentBuf.endUserInputTurnFragmentBuf(fbb)
}
